/**
 * SBV — YouTube's caption-editor export (SubViewer-derived, and confusingly not SubViewer).
 *
 * The whole format:
 *
 *     0:00:01.000,0:00:03.500
 *     First line
 *     Second line
 *     <blank>
 *
 * No cue numbers, no styling, no positioning, no header. `h:mm:ss.mmm` with a ONE-digit hour
 * and a COMMA as the range separator rather than `-->`, which is the only thing that
 * distinguishes it structurally from a numberless SRT.
 *
 * `Cue.index` stays null for every cue, by the contract's rule that it is "never a synthesized
 * value" — SBV has no cue numbers, so the model records that it has none rather than inventing
 * ordinals that a later SRT export would then present as if they came from the source.
 *
 * `[br]` appears in some exports as an explicit line break; YouTube's own reader treats it as
 * one, so this does too.
 */

import type {
  Cue,
  ParseOptions,
  ParseResult,
  ParserDefinition,
  RunContext,
} from '../../../contracts/cue-model.ts';
import { MS_GRID, makeCue, makeLine, makeRun, originFor } from '../../core/model/build.ts';
import { parseTimestamp, timeFormat } from '../../core/time/timestamp.ts';
import { Draft, safeSpan } from '../common/draft.ts';
import { blocksWithRecovery, type SourceLine, scanLines } from '../common/lines.ts';

const SBV_TIME = timeFormat('sbv');
/** Two clocks separated by a comma, and nothing else on the line. */
const RANGE_RE =
  /^\s*(\d{1,2}:\d{1,2}:\d{1,2}[.,]\d{1,3})\s*,\s*(\d{1,2}:\d{1,2}:\d{1,2}[.,]\d{1,3})\s*$/;

function isRange(text: string): boolean {
  return RANGE_RE.test(text);
}

export function parseSbv(text: string, opts: ParseOptions, ctx: RunContext): ParseResult {
  const draft = new Draft({ alloc: ctx.alloc, opts, sourceFormat: 'sbv', grid: MS_GRID });
  const lines = scanLines(text);
  const blocks = blocksWithRecovery(lines, (ls, at) => isRange((ls[at] as SourceLine).text));

  let ordinal = 0;
  let firstFailure: string | null = null;

  for (const block of blocks) {
    const head = block[0] as SourceLine;
    const m = RANGE_RE.exec(head.text);
    if (m === null) {
      const message = `expected an SBV time range like 0:00:01.000,0:00:03.500, found ${JSON.stringify(head.text.slice(0, 40))}`;
      draft.diag('malformed-timestamp', message, { sourceLine: head.no });
      firstFailure ??= `line ${head.no}: ${message}`;
      continue;
    }
    const start = parseTimestamp(m[1] as string, SBV_TIME);
    const end = parseTimestamp(m[2] as string, SBV_TIME);
    if (start === null || end === null) {
      const message = `unreadable SBV time range ${JSON.stringify(head.text.trim())}`;
      draft.diag('malformed-timestamp', message, { sourceLine: head.no });
      firstFailure ??= `line ${head.no}: ${message}`;
      continue;
    }

    const id = ctx.alloc.cue();
    const time = safeSpan(start as number, end as number, draft, {
      cueId: id,
      sourceLine: head.no,
    });
    const body = block
      .slice(1)
      .map((l) => l.text)
      .join('\n')
      .replace(/\[br\]/gi, '\n')
      .replace(/\n+$/, '');

    const textLines = body.split('\n').map((raw, i) =>
      makeLine(raw === '' ? [] : [makeRun({ id: ctx.alloc.run(), text: raw })], {
        breakBefore: i === 0 ? 'none' : 'hard',
      }),
    );

    const base = makeCue({
      id,
      time,
      lines: textLines.length === 0 ? [makeLine([])] : textLines,
      styleId: draft.defaultStyleId,
      trackId: draft.defaultTrackId,
      origin: { ...originFor(draft.inputId, 'parse', ordinal), sourceLine: head.no },
      granularity: 'coalesced',
    });
    const cue: Cue = {
      ...base,
      raw: opts.retainRaw
        ? {
            format: 'sbv',
            fields: { Start: m[1] as string, End: m[2] as string, Text: body },
            timeFields: ['Start', 'End'],
            stale: false,
          }
        : null,
    };
    draft.addCue(cue);
    ordinal++;
  }

  // Zero cues is not a failure — see the note in parse/srt.ts. B5 has two different messages.
  if (firstFailure !== null && draft.cues.length === 0) return draft.notRecognized(firstFailure);
  if (draft.cues.length === 0) {
    return draft.notRecognized('no `h:mm:ss.mmm,h:mm:ss.mmm` time range was found in this file');
  }
  return draft.ok();
}

export function sniffSbv(text: string, filename: string | null): number {
  const hit =
    /(^|\n)\s*\d{1,2}:\d{1,2}:\d{1,2}[.,]\d{1,3}\s*,\s*\d{1,2}:\d{1,2}:\d{1,2}[.,]\d{1,3}\s*(\n|$)/.test(
      text,
    );
  if (!hit) return 0;
  return /\.sbv$/i.test(filename ?? '') ? 1 : 0.9;
}

export const sbvParser: ParserDefinition = {
  format: 'sbv',
  sniff: sniffSbv,
  requiresFramerate: false,
  parse: parseSbv,
};
